import type { KakaoExportFormat } from '../types';

/**
 * Analytics for 카톡 성적표 (spec §6.5). Only coarse buckets and codes leave
 * the page: never names, message text, file names or exact message counts.
 */

export type MessageBucket = '<1k' | '1k-10k' | '10k-100k' | '100k+';
export type RoomKind = 'pair' | 'group';
export type FileSource = 'file' | 'email' | 'sample';

export interface KrcEventParams {
  readonly krc_file_selected: { readonly source: FileSource; readonly file_count: number };
  readonly krc_parsed: { readonly format: KakaoExportFormat; readonly room_kind: RoomKind; readonly message_bucket: MessageBucket };
  readonly krc_parse_failed: { readonly code: string };
  readonly krc_report_viewed: { readonly room_kind: RoomKind };
  readonly krc_image_saved: { readonly mode: 'download' | 'preview' | 'shared' };
  readonly krc_kakao_shared: null;
  readonly krc_intake_started: null;
  readonly krc_intake_received: { readonly file_count: number };
  readonly krc_intake_failed: { readonly reason: string };
}

export type KrcEventName = keyof KrcEventParams;
type GoogleTag = (...args: unknown[]) => void;

export function messageBucket(count: number): MessageBucket {
  if (!(count >= 1_000)) return '<1k';
  if (count < 10_000) return '1k-10k';
  if (count < 100_000) return '10k-100k';
  return '100k+';
}

/** File counts are small, but cap them so odd uploads cannot fingerprint anyone. */
export function fileCountParam(count: number): number {
  if (!Number.isFinite(count) || count < 0) return 0;
  return Math.min(10, Math.round(count));
}

export function roomKindForPeople(people: number): RoomKind {
  return people === 2 ? 'pair' : 'group';
}

function defaultTag(): GoogleTag | undefined {
  return typeof window === 'undefined' ? undefined : window.gtag;
}

export function trackKrc<K extends KrcEventName>(name: K, params: KrcEventParams[K], tag: GoogleTag | undefined = defaultTag()): void {
  if (!tag) return;
  try {
    if (params) tag('event', name, { ...params });
    else tag('event', name);
  } catch {
    // Analytics must never break the tool.
  }
}
